import { Disclosure } from "@headlessui/react";
import {
  ChevronDoubleDownIcon,
  ReceiptPercentIcon,
} from "@heroicons/react/24/solid";
import { useRecoilState, useRecoilValue } from "recoil";
import CloseIconButton from "../../../../../components/CloseIconButton";
import {
  addFromVoidedState,
  getDataById,
  hasExistingState,
  voidedData,
} from "../../InvoiceCapture/atoms/atoms";
import { VoidedView } from "./VoidedView";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import { useEffect, useState } from "react";
import { useVoidedAddInvoice } from "../hooks/useVoidedAddInvoice";
import { toast } from "../../../../../components/toastNotification";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { selectedVIPTab } from "../../InvoiceManager/atoms/atoms";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { VendorInvoiceTabs } from "../../VendorInvoiceTabs";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import { SubmittedInvoiceActivityHistoryView } from "../../SubmittedToAcumatica/view/SubmittedInvoiceActivityHistoryView";
import {
  isAddDebitAdjusment,
  isAttachDebitAdjusment,
} from "../../Completed/atom/atoms";
import DebitAdjustmentButton from "components/buttons/DebitAdjustmentButton";

interface VoidedProp {
  cancelShow;
  rowData;
  activityHistoryData;
}

export const VoidedDetails: React.FC<VoidedProp> = ({
  cancelShow,
  rowData,
  activityHistoryData,
}) => {
  const [, setIsAddDebitAdj] = useRecoilState(isAddDebitAdjusment);
  const [, setIsAttachDebitAdj] = useRecoilState(isAttachDebitAdjusment);
  const [voidedInvoiceDetails, setVoidedInvoiceDetails] =
    useRecoilState<any>(voidedData);
  const [openModal, setOpenModal] = useState(false);
  const [, setGetInvoiceByIdData] = useRecoilState<any>(getDataById);
  const queryClient = useQueryClient();
  const [, setAddFromVoided] = useRecoilState<any>(addFromVoidedState);
  const history = useHistory();
  const [, setSelectedIndex] = useRecoilState(selectedVIPTab);
  const [hasExistingTransaction, setHasExistingTransaction] =
    useRecoilState(hasExistingState);

  //#region Permission
  const userPermissions = useRecoilValue(permissionsSelector);
  const currentTabs = VendorInvoiceTabs.filter((tabs) =>
    userPermissions.includes(tabs.permission)
  );
  const addPermission = PERMISSIONS.permission_vendorinvoice_add;

  const {
    mutate: addVendorInvoice,
    isSuccess: addVendorInvoiceSuccess,
    data: addVendorInvoiceResult,
    reset: resetAddVendorInvoiceData,
  } = useVoidedAddInvoice();

  const handleSave = () => {
    const VendorInvoiceData = {
      id: rowData.invoiceDetailsId,
    };
    addVendorInvoice(VendorInvoiceData);
    setAddFromVoided(true);
    setOpenModal(false);
  };

  const handleLink = () => {
    const url = "/vendortab/invoice/pending";
    history.push(url);
    const index = currentTabs.findIndex((c) => c.to === url);
    setSelectedIndex(index);
  };

  //Toasters
  useEffect(() => {
    if (addVendorInvoiceSuccess && addVendorInvoiceResult) {
      if (
        addVendorInvoiceResult.status === 201 ||
        addVendorInvoiceResult.status === 200
      ) {
        toast.fire("Success", "Vendor invoice added successfully.", "success");
        setGetInvoiceByIdData(addVendorInvoiceResult?.data);
        handleLink();
        queryClient.invalidateQueries("GetInvoices");
      }
      resetAddVendorInvoiceData();
    }
  }, [addVendorInvoiceSuccess]);

  return (
    <>
      <div className="box-shadow p-4 border-quinary border-l-2 p-2">
        <div>
          <div className="relative -top-6 -right-4">
            <CloseIconButton
              onClick={() => {
                cancelShow();
                setVoidedInvoiceDetails(undefined);
                setHasExistingTransaction(false);
                setIsAddDebitAdj(false);
                setIsAttachDebitAdj(false);
              }}
            />
          </div>
        </div>
        <div>
          <div>
            <div className="flex justify-between items-center py-2 border-secondary border-b-2 ">
              <div>
                <div className="flex gap-2 items-center ">
                  <div>
                    <ReceiptPercentIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
                  </div>
                  <div className="flex flex-col leading tight">
                    <h3 className="text-xs font-medium text-gray-500 ">
                      Vendor Invoice Processing
                    </h3>
                    <h1 className="mr-auto font-bold text-primary text-md uppercase pb-2">
                      Batch List - {rowData?.id}
                    </h1>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-xs space-y-2 my-2">
              <div className="ml-auto p-2 flex rounded box-shadow justify-between ">
                <div className="space-x-4 mr-6 self-center text-pink-500 font-bold">
                  {rowData?.vendorName} - {rowData?.vendorCode}
                </div>
                {userPermissions.includes(addPermission) && (
                  <AddButton
                    disabled={hasExistingTransaction}
                    buttonText={"Re-create Invoice"}
                    className={undefined}
                    onClick={() => {
                      setOpenModal(true);
                    }}
                  />
                )}
              </div>
            </div>

            <div className="">
              <div className="mt-2 bg-white p-4 box-shadow rounded-md">
                <div className="text-quinary pb-4 font-bold ">
                  Vendor Information
                </div>
                <div className="grid grid-cols-3 items-center">
                  <div className="self-start">
                    <div className="expandedLabelVIP fontExpand">
                      {rowData?.vendorName}
                    </div>
                    <div>
                      <div className="expandedDataVIP fontExpand ">
                        {rowData?.vendorCode}
                      </div>
                    </div>
                  </div>
                  <div className="self-start">
                    <div className="expandedLabelVIP fontExpand ml-4">
                      {rowData?.officeName}
                    </div>
                    <div className="expandedLabelVIP fontExpand ml-4">
                      {rowData?.officeStreet} {", "} {rowData?.officeCity}{" "}
                      {", "}
                      {rowData?.officeStateCode}
                    </div>
                    <div className="expandedLabelVIP fontExpand ml-4">
                      {rowData?.officeZipcode}
                    </div>
                  </div>
                  <div className="self-start">
                    <div className="expandedLabelVIP fontExpand ml-4">
                      Vendor ID: {rowData?.vendorCode}
                    </div>
                    <div className="expandedLabelVIP fontExpand ml-4">
                      Address Code: {rowData?.vendorCode}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="">
              {voidedInvoiceDetails &&
                voidedInvoiceDetails?.map((data, index) => {
                  return (
                    <>
                      <Disclosure
                        defaultOpen={
                          index === voidedInvoiceDetails.length - 1
                            ? true
                            : false
                        }
                      >
                        {({ open }) => (
                          <>
                            <div className="mt-2 bg-white rounded-lg box-shadow ">
                              <div className="flex flex-row justify-between">
                                <div className="text-quinary font-bold font-sans py-2 px-3">
                                  Invoice Details {index + 1} - Voided
                                </div>
                                <div className="flex flex-row justify-end">
                                  {/* <div className=" mt-2 h-6">
                                    {userPermissions.includes(approver) ||
                                    userPermissions.includes(analyst) ? (
                                      <div className="flex flex-row justify-end">
                                        <DebitAdjustmentButton />
                                      </div>
                                    ) : (
                                      ""
                                    )}
                                  </div> */}
                                  <div>
                                    <Disclosure.Button className="disclosureBtn ml-2 justify-end">
                                      <ChevronDoubleDownIcon
                                        className={`${
                                          open ? "transform rotate-180" : ""
                                        }  text-gray-500 w-5 h-5 mr-4`}
                                      />
                                    </Disclosure.Button>
                                  </div>
                                </div>
                              </div>
                              <Disclosure.Panel className="text-xxs">
                                <div className="">
                                  <VoidedView rowData={data} />
                                </div>
                              </Disclosure.Panel>
                            </div>
                          </>
                        )}
                      </Disclosure>
                    </>
                  );
                })}
            </div>

            <div className="w-full bg-white">
              <Disclosure>
                {({ open }) => (
                  <>
                    <div className=" bg-white rounded-lg box-shadow">
                      <div className="flex flex-row justify-between mt-2">
                        <div className="text-quinary font-bold font-sans py-2 px-3">
                          Activity History
                        </div>
                        <div>
                          <Disclosure.Button className="disclosureBtn ml-2 justify-end">
                            <ChevronDoubleDownIcon
                              className={`${
                                open ? "transform rotate-180" : ""
                              }  text-gray-500 w-5 h-5 mr-4`}
                            />
                          </Disclosure.Button>
                        </div>
                      </div>
                      <Disclosure.Panel className="text-xxs">
                        <SubmittedInvoiceActivityHistoryView
                          activityHistoryData={activityHistoryData}
                        />
                      </Disclosure.Panel>
                    </div>
                  </>
                )}
              </Disclosure>
            </div>
          </div>
        </div>

        {/*Re-create confirm modal */}
        {openModal && (
          <ConfirmBoxModal
            ModalTitle={"Re-create Invoice"}
            qConfirmation={"Are you sure you want to re-create invoice?"}
            isOpen={openModal}
            onClose={() => {
              setAddFromVoided(false);
              setOpenModal(false);
            }}
            onClickFunction={() => {
              handleSave();
            }}
            cancelButtonRef={undefined}
          />
        )}
      </div>
    </>
  );
};
